<script setup lang="ts">
import { formatInTZ, type TimeSlot } from '@sera/core';

interface Props {
  slots: TimeSlot[];
  selectedSlot?: TimeSlot | null;
  timeZone: string;
  loading?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  selectedSlot: null,
  loading: false,
  disabled: false,
});

const emit = defineEmits<{ select: [slot: TimeSlot] }>();

// Full-literal Tailwind class strings — never interpolated (CLAUDE.md constraint)
// Slot label is single-line: `whitespace-nowrap` keeps the text on one line
// and `h-11` keeps every button the same height regardless of label length.
// Overflow at narrow widths is solved by the parent grid (`grid-cols-3
// sm:grid-cols-2 lg:grid-cols-3` — fewer columns at the tightest band so
// each cell has room for the longest label), not by wrapping text inside
// the button. Per-user direction: prefer wrapping the *grid* over wrapping
// the label, so all buttons stay one-line and uniform-height
// (PODZ-1697/-1698/-1699 follow-up).
//
// SLOT_DISABLED is applied when EITHER the global `disabled` prop is true
// OR the individual slot carries `disabled: true` from the backend
// availability_controller (quick-260528-od4 / PODZ-1702). Both paths share
// the same class constant so they cannot diverge visually.
const SLOT_BASE =
  'w-full h-11 rounded-md text-2xs sm:text-xs border transition-colors focus:outline-none focus:ring-2 focus:ring-primary tabular-nums whitespace-nowrap';
const SLOT_DEFAULT =
  'bg-surface border-border-strong text-foreground hover:bg-primary-light';
const SLOT_SELECTED = 'bg-primary text-white border-primary';
const SLOT_DISABLED = 'opacity-50 cursor-not-allowed pointer-events-none';

function isSelected(slot: TimeSlot): boolean {
  return props.selectedSlot?.id === slot.id;
}

function isDisabled(slot: TimeSlot): boolean {
  return props.disabled || slot.disabled === true;
}

function classFor(slot: TimeSlot): string {
  let cls = SLOT_BASE;
  if (isSelected(slot)) cls += ' ' + SLOT_SELECTED;
  else cls += ' ' + SLOT_DEFAULT;
  if (isDisabled(slot)) cls += ' ' + SLOT_DISABLED;
  return cls;
}

// Format a single time as `8 AM` (on the hour) or `8:30 AM` (with minutes).
// Format once with `hour + minute`, then strip the `:00` only when the slot
// lands on the hour. Done by regex on the formatted string rather than via
// a separate Intl call, since `Intl.DateTimeFormat({ minute: '2-digit' })`
// alone produces inconsistent output across runtimes (Node vs happy-dom).
function formatSlotTime(iso: string, tz: string): string {
  const full = formatInTZ(iso, tz, {
    hour: 'numeric',
    minute: '2-digit',
  });
  // Matches `:00` followed by a space (before AM/PM) or end of string.
  return full.replace(/:00(\s|$)/, '$1');
}

function labelFor(slot: TimeSlot): string {
  const start = formatSlotTime(slot.starts_at, props.timeZone);
  if (!slot.ends_at) return start;
  const end = formatSlotTime(slot.ends_at, props.timeZone);
  return `${start} – ${end}`;
}

function onSelect(slot: TimeSlot): void {
  if (isDisabled(slot)) return;
  emit('select', slot);
}
</script>

<template>
  <div v-if="loading">
    <div class="h-11 rounded-md bg-canvas animate-pulse mb-2" />
    <div class="h-11 rounded-md bg-canvas animate-pulse mb-2" />
    <div class="h-11 rounded-md bg-canvas animate-pulse" />
  </div>
  <!-- Responsive column count: 3 on phone (existing density for 1-hour
       windows), 2 on tablet (where the modal naturally narrows to ~720px
       and 3 cols would crush 3-hour labels), back to 3 on desktop (modal
       is `size="xl"` 1200px so the slot column is wide enough to absorb
       any label). The grid wraps so all buttons keep uniform h-11. -->
  <div
    v-else
    role="list"
    class="grid grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 gap-2"
  >
    <div v-for="slot in slots" :key="slot.id" role="listitem">
      <button
        type="button"
        :class="classFor(slot)"
        :aria-pressed="isSelected(slot) ? 'true' : 'false'"
        :aria-disabled="isDisabled(slot) ? 'true' : 'false'"
        :disabled="isDisabled(slot)"
        @click="onSelect(slot)"
      >
        {{ labelFor(slot) }}
      </button>
    </div>
  </div>
</template>
